import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, money, payWords, today } from '../../utils/format';
import { Chip, Empty, ErrorNotice, Loading, PageHeader, Stat, Table } from '../../components/ui';
import { DateInput, Drawer, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';
import { VoidDialog } from '../../components/ui/VoidDialog';
import { useToast } from '../../components/ui/Toast';

type Side = 'CUSTOMER' | 'SUPPLIER';
type Kind = 'ADVANCE' | 'ADVANCE_REFUND';

interface BalanceRow {
  party_type: Side;
  party_id: string;
  party_name: string;
  phone: string | null;
  added: string | null;
  used: string;
  refunded: string;
  balance: string;
  last_moved: string;
  days_held: number | null;
}

interface AdvanceDoc {
  id: string;
  doc_no: string;
  doc_date: string;
  kind: Kind;
  direction: 'IN' | 'OUT';
  party_type: Side;
  party_id: string;
  party_name: string;
  mode: string;
  amount: string;
  purpose: string | null;
  reference_no: string | null;
  status: string;
  branch_name: string;
}

interface LedgerRow {
  date: string;
  doc_no: string;
  what: string;
  detail: string | null;
  mode: string | null;
  added: string;
  taken_out: string;
  balance: string;
  source_doc_type: string;
  source_doc_id: string | null;
}

/** Words for each side, so the same screen reads right for both. */
const SIDE = {
  CUSTOMER: {
    take: 'Take customer advance',
    refund: 'Refund to customer',
    who: 'Customer',
    held: 'Held for customers',
    heldHint: 'Their money, waiting for a bill',
    takeTitle: 'Advance from a customer',
    takeHint: 'Kept in their name until a bill uses it. Spent at the till as “Use advance”.',
    refundTitle: 'Refund a customer’s advance',
    refundHint: 'Hands back money they paid ahead. No more than they hold.',
    givenLabel: 'Advance received',
  },
  SUPPLIER: {
    take: 'Pay supplier advance',
    refund: 'Supplier returns advance',
    who: 'Supplier',
    held: 'Paid to suppliers',
    heldHint: 'Ours, until their goods arrive',
    takeTitle: 'Advance to a supplier',
    takeHint: 'Kept in their name until a purchase from them uses it.',
    refundTitle: 'Money back from a supplier',
    refundHint: 'An advance the supplier returns. No more than they hold.',
    givenLabel: 'Advance paid',
  },
} as const;

/**
 * Advances, both ways: money a customer pays ahead of buying, and money the
 * shop pays a supplier ahead of a delivery. Who holds what, every movement on
 * each person's advance, and the receipts and vouchers themselves.
 */
export function AdvancesPage() {
  const { can } = usePermission();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as 'held' | 'documents' | null) ?? 'held';
  const side = (params.get('side') as Side | 'ALL' | null) ?? 'ALL';
  const setQuery = (next: Record<string, string>): void => setParams({ tab, side, ...next }, { replace: true });

  const [entering, setEntering] = useState<{ side: Side; kind: Kind; partyId?: string } | null>(null);
  const [ledgerOf, setLedgerOf] = useState<{ side: Side; id: string } | null>(null);
  const [voiding, setVoiding] = useState<AdvanceDoc | null>(null);
  const [error, setError] = useState<unknown>(null);
  const client = useQueryClient();
  const toast = useToast();
  const seesSuppliers = can('supplier.view');
  const seesCustomers = can('customer.view');

  const balances = useQuery({
    queryKey: ['advances', 'balances'],
    queryFn: () => api.get<{ data: BalanceRow[] }>('/advances/balances'),
  });
  const docs = useQuery({
    queryKey: ['advances', 'documents'],
    queryFn: () => api.get<{ data: AdvanceDoc[] }>('/advances'),
  });

  const allRows = balances.data?.data ?? [];
  const visible = (t: Side): boolean => (t === 'CUSTOMER' ? seesCustomers : seesSuppliers);
  const rows = allRows.filter((r) => (side === 'ALL' || r.party_type === side) && visible(r.party_type));
  const total = (s: Side): number =>
    allRows.filter((r) => r.party_type === s).reduce((a, r) => a + Number(r.balance), 0);
  const holders = (s: Side): number => allRows.filter((r) => r.party_type === s && Number(r.balance) > 0).length;
  const docRows = (docs.data?.data ?? []).filter(
    (d) => (side === 'ALL' || d.party_type === side) && visible(d.party_type),
  );

  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/payments/${id}/cancel`, { reason }),
    onSuccess: () => {
      toast(`${voiding?.doc_no ?? 'Advance'} cancelled`, 'The money is reversed in cash or bank.');
      void client.invalidateQueries({ queryKey: ['advances'] });
      setVoiding(null);
      setError(null);
    },
    onError: (e) => {
      setError(e);
      setVoiding(null);
    },
  });

  const sides: Side[] = (['CUSTOMER', 'SUPPLIER'] as Side[]).filter((x) => visible(x));

  return (
    <>
      <PageHeader
        title="Advances"
        subtitle="Money a customer pays ahead of buying, and money paid to a supplier ahead of a delivery — held in their name until a bill uses it."
        actions={
          can('payment.create') ? (
            <>
              {seesCustomers ? (
                <button className="btn-primary" onClick={() => setEntering({ side: 'CUSTOMER', kind: 'ADVANCE' })}>
                  {SIDE.CUSTOMER.take}
                </button>
              ) : null}
              {seesSuppliers ? (
                <button className="btn-ghost" onClick={() => setEntering({ side: 'SUPPLIER', kind: 'ADVANCE' })}>
                  {SIDE.SUPPLIER.take}
                </button>
              ) : null}
            </>
          ) : null
        }
      />
      <ErrorNotice error={error ?? balances.error ?? docs.error} />

      <div className={`mb-4 grid gap-3 ${sides.length > 1 ? 'sm:grid-cols-2' : ''}`}>
        {sides.map((s) => (
          <Stat
            key={s}
            label={SIDE[s].held}
            value={money(total(s).toFixed(2))}
            hint={`${holders(s)} ${holders(s) === 1 ? (s === 'CUSTOMER' ? 'customer' : 'supplier') : s === 'CUSTOMER' ? 'customers' : 'suppliers'} · ${SIDE[s].heldHint}`}
          />
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-end justify-between gap-3 border-b border-silver-100">
        <div className="flex gap-1" role="tablist">
          {(
            [
              ['held', 'Who holds what'],
              ['documents', 'Receipts and vouchers'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                tab === key
                  ? 'border-gold-600 font-semibold text-ink'
                  : 'border-transparent text-ink-soft hover:text-ink'
              }`}
              onClick={() => setParams({ tab: key, side }, { replace: true })}
            >
              {label}
            </button>
          ))}
        </div>
        {sides.length > 1 ? (
          <div className="mb-2 flex gap-1 text-xs">
            {(
              [
                ['ALL', 'Everyone'],
                ['CUSTOMER', 'Customers'],
                ['SUPPLIER', 'Suppliers'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                className={`rounded px-2.5 py-1 ${side === key ? 'bg-velvet text-white' : 'bg-silver-50 text-ink-soft hover:text-ink'}`}
                onClick={() => setQuery({ side: key })}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {tab === 'held' ? (
        <>
          {balances.isLoading ? <Loading /> : null}
          {!balances.isLoading && rows.length === 0 ? (
            <Empty
              message="Nobody holds an advance yet."
              hint="Take a customer's booking money, or pay a supplier ahead of a delivery, and it shows here until a bill uses it."
            />
          ) : null}
          {rows.length > 0 ? (
            <Table
              head={[
                { label: 'Name', align: 'left' },
                'Type',
                { label: 'Given', align: 'right' },
                { label: 'Used on bills', align: 'right' },
                { label: 'Refunded', align: 'right' },
                { label: 'Balance', align: 'right' },
                { label: 'Held for', align: 'right' },
                '',
              ]}
            >
              {rows.map((r) => (
                <tr key={`${r.party_type}:${r.party_id}`} className={Number(r.balance) === 0 ? 'opacity-60' : ''}>
                  <td className="td">
                    <button
                      className="text-left font-medium underline decoration-silver-200 underline-offset-2 hover:decoration-gold-600"
                      onClick={() => setLedgerOf({ side: r.party_type, id: r.party_id })}
                    >
                      {r.party_name}
                    </button>
                    {r.phone ? <span className="num block text-2xs text-ink-soft">{r.phone}</span> : null}
                  </td>
                  <td className="td text-xs">{r.party_type === 'CUSTOMER' ? 'Customer' : 'Supplier'}</td>
                  <td className="td num text-right">{money(r.added ?? '0')}</td>
                  <td className="td num text-right">{Number(r.used) > 0 ? money(r.used) : '—'}</td>
                  <td className="td num text-right">{Number(r.refunded) > 0 ? money(r.refunded) : '—'}</td>
                  <td className="td num text-right font-semibold">{money(r.balance)}</td>
                  <td className="td num text-right text-xs text-ink-soft">
                    {Number(r.balance) > 0 && r.days_held !== null ? `${r.days_held} d` : '—'}
                  </td>
                  <td className="td whitespace-nowrap text-right text-xs">
                    <button className="underline" onClick={() => setLedgerOf({ side: r.party_type, id: r.party_id })}>
                      History
                    </button>
                    {can('payment.create') && Number(r.balance) > 0 ? (
                      <button
                        className="ml-3 underline"
                        onClick={() => setEntering({ side: r.party_type, kind: 'ADVANCE_REFUND', partyId: r.party_id })}
                      >
                        Refund
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </Table>
          ) : null}
        </>
      ) : (
        <>
          {docs.isLoading ? <Loading /> : null}
          {!docs.isLoading && docRows.length === 0 ? <Empty message="No advance receipts or vouchers yet." /> : null}
          {docRows.length > 0 ? (
            <Table
              head={[
                'Date',
                'Document',
                { label: 'Name', align: 'left' },
                'What',
                { label: 'Purpose', align: 'left' },
                'Mode',
                { label: 'Amount', align: 'right' },
                '',
              ]}
            >
              {docRows.map((d) => (
                <tr key={d.id} className={d.status === 'CANCELLED' ? 'opacity-60' : ''}>
                  <td className="td">{date(d.doc_date)}</td>
                  <td className="td num">{d.doc_no}</td>
                  <td className="td">{d.party_name}</td>
                  <td className="td">
                    {d.status === 'CANCELLED' ? (
                      <Chip tone="mute">cancelled</Chip>
                    ) : d.kind === 'ADVANCE_REFUND' ? (
                      <Chip tone="warn">refund</Chip>
                    ) : (
                      <Chip tone="good">{d.party_type === 'CUSTOMER' ? 'received' : 'paid'}</Chip>
                    )}
                  </td>
                  <td className="td text-xs">{d.purpose ?? '—'}</td>
                  <td className="td text-xs">
                    {payWords(d.mode)}
                    {d.reference_no ? <span className="block text-ink-soft">{d.reference_no}</span> : null}
                  </td>
                  <td className={`td num text-right ${d.direction === 'IN' ? 'text-success' : ''}`}>
                    {d.direction === 'IN' ? '+' : '−'}
                    {money(d.amount)}
                  </td>
                  <td className="td whitespace-nowrap text-right text-xs">
                    <Link className="underline" to={`/advances/${d.id}/print`}>
                      Print
                    </Link>
                    {d.status !== 'CANCELLED' && can('document.void') ? (
                      <button className="ml-3 text-danger underline" onClick={() => setVoiding(d)}>
                        Cancel
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </Table>
          ) : null}
        </>
      )}

      {entering ? (
        <AdvanceDrawer
          side={entering.side}
          kind={entering.kind}
          partyId={entering.partyId}
          holders={allRows}
          onClose={() => setEntering(null)}
        />
      ) : null}
      {ledgerOf ? (
        <LedgerDrawer
          side={ledgerOf.side}
          partyId={ledgerOf.id}
          onClose={() => setLedgerOf(null)}
          onAct={(kind) => {
            setEntering({ side: ledgerOf.side, kind, partyId: ledgerOf.id });
            setLedgerOf(null);
          }}
        />
      ) : null}
      <VoidDialog
        docNo={voiding?.doc_no ?? null}
        what="advance"
        effect={
          voiding?.kind === 'ADVANCE_REFUND'
            ? 'The refund is reversed, so the advance is held again.'
            : 'The money is reversed in cash or bank and comes off their advance. An advance a bill has already used cannot be cancelled.'
        }
        busy={cancel.isPending}
        onCancel={() => setVoiding(null)}
        onConfirm={(reason) => voiding && cancel.mutate({ id: voiding.id, reason })}
      />
    </>
  );
}

function AdvanceDrawer({
  side,
  kind,
  partyId: initialParty,
  holders,
  onClose,
}: {
  side: Side;
  kind: Kind;
  partyId?: string;
  holders: BalanceRow[];
  onClose: () => void;
}) {
  const words = SIDE[side];
  const refund = kind === 'ADVANCE_REFUND';
  const client = useQueryClient();
  const navigate = useNavigate();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();
  const [key] = useState(() => crypto.randomUUID());
  const [form, setForm] = useState({
    partyId: initialParty ?? '',
    docDate: today(),
    amount: '',
    mode: 'CASH',
    purpose: '',
    referenceNo: '',
    branchId: '',
  });

  const parties = useQuery({
    queryKey: [side === 'CUSTOMER' ? 'customers' : 'suppliers', ''],
    queryFn: () => (side === 'CUSTOMER' ? endpoints.parties.customers() : endpoints.parties.suppliers()),
    enabled: !refund,
  });
  const options = useMemo(
    () =>
      refund
        ? holders
            .filter((h) => h.party_type === side && Number(h.balance) > 0)
            .map((h) => ({ value: h.party_id, label: `${h.party_name} — holds ${money(h.balance)}` }))
        : ((parties.data?.data ?? []) as unknown as { id: string; name: string; phone?: string }[]).map((p) => ({
            value: p.id,
            label: p.phone ? `${p.name} · ${p.phone}` : p.name,
          })),
    [refund, holders, side, parties.data],
  );

  const balance = useQuery({
    queryKey: ['advances', 'balance', side, form.partyId],
    queryFn: () => api.get<{ balance: string }>(`/advances/balance/${side}/${form.partyId}`),
    enabled: !!form.partyId,
  });
  const heldNow = Number(balance.data?.balance ?? 0);
  const amount = Number(form.amount || 0);
  const after = refund ? heldNow - amount : heldNow + amount;
  const tooMuch = refund && amount > heldNow;

  const post = useMutation({
    mutationFn: () =>
      api.post<{ paymentId: string; docNo: string }>(
        '/advances',
        {
          branchId: form.branchId || defaultBranchId,
          docDate: form.docDate,
          partyType: side,
          partyId: form.partyId,
          kind,
          mode: form.mode,
          amount: Number(form.amount).toFixed(2),
          ...(form.purpose.trim() ? { purpose: form.purpose.trim() } : {}),
          ...(form.referenceNo.trim() ? { referenceNo: form.referenceNo.trim() } : {}),
        },
        { 'idempotency-key': key },
      ),
    onSuccess: (r) => {
      void client.invalidateQueries({ queryKey: ['advances'] });
      void client.invalidateQueries({ queryKey: ['customer-advance'] });
      onClose();
      navigate(`/advances/${r.paymentId}/print`);
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!form.partyId || !amount || tooMuch) return;
    post.mutate();
  };

  return (
    <Drawer
      title={refund ? words.refundTitle : words.takeTitle}
      subtitle={refund ? words.refundHint : words.takeHint}
      onClose={onClose}
    >
      <ErrorNotice error={post.error} />
      <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
        <Field label={words.who} required>
          <Select
            value={form.partyId}
            onChange={(v) => setForm({ ...form, partyId: v })}
            options={options}
            placeholder={refund ? 'Choose who is being refunded' : `Choose the ${words.who.toLowerCase()}`}
          />
        </Field>

        {form.partyId ? (
          <div className="rounded-md border border-silver-100 bg-silver-50/60 px-3 py-2 text-sm">
            <div className="flex justify-between">
              <span className="text-ink-soft">
                {side === 'CUSTOMER' ? 'Holds with the shop now' : 'Holds of ours now'}
              </span>
              <span className="num font-medium">{money(heldNow.toFixed(2))}</span>
            </div>
            {amount > 0 ? (
              <div className="mt-1 flex justify-between">
                <span className="text-ink-soft">After this</span>
                <span className={`num font-semibold ${tooMuch ? 'text-danger' : ''}`}>{money(after.toFixed(2))}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount" required error={tooMuch ? `No more than ${money(heldNow.toFixed(2))}` : null}>
            <NumberInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} suffix="₹" />
          </Field>
          <Field label="Date" required>
            <DateInput value={form.docDate} onChange={(v) => setForm({ ...form, docDate: v })} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={refund ? 'Paid back by' : side === 'CUSTOMER' ? 'Received by' : 'Paid by'} required>
            <Select
              value={form.mode}
              onChange={(v) => setForm({ ...form, mode: v })}
              options={[
                { value: 'CASH', label: 'Cash' },
                { value: 'UPI', label: 'UPI' },
                { value: 'BANK', label: 'Bank transfer' },
                { value: 'CARD', label: 'Card' },
                { value: 'CHEQUE', label: 'Cheque' },
              ]}
            />
          </Field>
          <Field label="Reference" hint="UPI, cheque or NEFT number.">
            <TextInput value={form.referenceNo} onChange={(v) => setForm({ ...form, referenceNo: v })} maxLength={64} />
          </Field>
        </div>

        <Field
          label={refund ? 'Reason' : 'What it is for'}
          hint={
            refund
              ? 'Printed on the voucher.'
              : side === 'CUSTOMER'
                ? 'Printed on their receipt.'
                : 'Printed on the voucher.'
          }
        >
          <TextInput
            value={form.purpose}
            onChange={(v) => setForm({ ...form, purpose: v })}
            maxLength={200}
            placeholder={
              refund
                ? 'Order cancelled'
                : side === 'CUSTOMER'
                  ? 'Booking — bridal necklace set'
                  : 'Advance for 100 g 22K chains'
            }
          />
        </Field>

        {branches.length > 1 ? (
          <Field label="Branch" required>
            <Select
              value={form.branchId || (defaultBranchId ?? '')}
              onChange={(v) => setForm({ ...form, branchId: v })}
              options={branches.map((b) => ({ value: b.id, label: b.name }))}
            />
          </Field>
        ) : null}

        <div className="flex gap-2 pt-2">
          <button className="btn-primary" disabled={!form.partyId || !amount || tooMuch || post.isPending}>
            {post.isPending ? 'Recording…' : refund ? 'Record refund and print' : 'Record and print receipt'}
          </button>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Drawer>
  );
}

/** Where a movement came from, linked to its own document where it has one. */
function docLink(r: LedgerRow): string | null {
  if (!r.source_doc_id) return null;
  if (r.source_doc_type === 'SALE') return `/sales/${r.source_doc_id}/print`;
  if (r.source_doc_type === 'PURCHASE' || r.source_doc_type === 'OLD_GOLD')
    return `/purchases/${r.source_doc_id}/print`;
  if (r.source_doc_type === 'ADVANCE') return `/advances/${r.source_doc_id}/print`;
  return null;
}

function LedgerDrawer({
  side,
  partyId,
  onClose,
  onAct,
}: {
  side: Side;
  partyId: string;
  onClose: () => void;
  onAct: (kind: Kind) => void;
}) {
  const { can } = usePermission();
  const ledger = useQuery({
    queryKey: ['advances', 'ledger', side, partyId],
    queryFn: () =>
      api.get<{ party: string; balance: string; rows: LedgerRow[] }>(`/advances/ledger/${side}/${partyId}`),
  });
  const rows = ledger.data?.rows ?? [];
  return (
    <Drawer
      wide
      title={ledger.data ? `${ledger.data.party} — advance` : 'Advance history'}
      subtitle={
        ledger.data
          ? `${side === 'CUSTOMER' ? 'Holds with the shop' : 'Holds of ours'}: ${money(ledger.data.balance)}`
          : undefined
      }
      onClose={onClose}
      footer={
        can('payment.create') ? (
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" onClick={() => onAct('ADVANCE')}>
              {side === 'CUSTOMER' ? 'Take more advance' : 'Pay more advance'}
            </button>
            {Number(ledger.data?.balance ?? 0) > 0 ? (
              <button className="btn-ghost" onClick={() => onAct('ADVANCE_REFUND')}>
                {SIDE[side].refund}
              </button>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {ledger.isLoading ? <Loading /> : null}
      <ErrorNotice error={ledger.error} />
      {rows.length === 0 && !ledger.isLoading ? <Empty message="No movements yet." /> : null}
      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="th text-left">Date</th>
                <th className="th text-left">Document</th>
                <th className="th text-left">What happened</th>
                <th className="th text-right">In</th>
                <th className="th text-right">Out</th>
                <th className="th text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const link = docLink(r);
                return (
                  <tr key={i}>
                    <td className="td whitespace-nowrap">{date(r.date)}</td>
                    <td className="td num whitespace-nowrap">
                      {link ? (
                        <Link className="underline" to={link}>
                          {r.doc_no}
                        </Link>
                      ) : (
                        r.doc_no
                      )}
                    </td>
                    <td className="td">
                      {r.what}
                      {r.detail ? <span className="block text-2xs text-ink-soft">{r.detail}</span> : null}
                    </td>
                    <td className="td num text-right">{Number(r.added) > 0 ? money(r.added) : ''}</td>
                    <td className="td num text-right">{Number(r.taken_out) > 0 ? money(r.taken_out) : ''}</td>
                    <td className="td num text-right font-medium">{money(r.balance)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Drawer>
  );
}
